import React from "react";
import { Collection } from "@/services/collectionsService";

interface CollectionCardProps {
  collection: Collection;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onShare: () => void;
  onUpload: () => void;
}

export const CollectionCard: React.FC<CollectionCardProps> = ({
  collection,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onShare,
  onUpload,
}) => {
  const getCollectionIcon = () => {
    if (collection.briefId) {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
        <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
      </svg>
    );
  };

  return (
    <div
      onClick={onSelect}
      className={`
        group relative px-4 py-3 rounded-lg cursor-pointer transition-all duration-200
        ${isSelected
          ? "bg-cyan-50 border-2 border-cyan-500 shadow-xs"
          : "bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xs"
        }
      `}
    >
      {/* Name row */}
      <div className="flex items-center gap-3">
        <div className="shrink-0">
          {getCollectionIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${isSelected ? "text-cyan-900" : "text-gray-900"}`}>
            {collection.name || "Unnamed Collection"}
          </p>
        </div>
        {isSelected && (
          <svg className="w-4 h-4 text-cyan-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
      </div>

      {/* Count + actions row */}
      <div className="flex items-center justify-between mt-1.5 pl-7">
        <p className={`text-xs ${isSelected ? "text-cyan-600" : "text-gray-500"}`}>
          {collection.itemCount} {collection.itemCount === 1 ? "item" : "items"}
        </p>

        <div className={`
          flex items-center gap-0.5
          ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
          transition-opacity duration-150
        `}>
          <button
            onClick={(e) => { e.stopPropagation(); onUpload(); }}
            className="p-1 rounded hover:bg-cyan-100 text-gray-400 hover:text-cyan-600 transition-colors"
            title="Upload to collection"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onShare(); }}
            className={`p-1 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-500 transition-colors ${
              collection.itemCount === 0 ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title={collection.itemCount === 0 ? "Collection is empty" : "Share collection"}
            disabled={collection.itemCount === 0}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            title="Edit collection"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
            title="Delete collection"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
